import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Lock, CalendarDays, Mail, Trash2, Plus, Users, Image, Send, Upload, FileText, History, Camera, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_da037825-faa5-420e-a579-84f9393da584/artifacts/qxg7ev86_redeemlogo.jpeg";

function PasscodeEntry({ onVerified }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try { await axios.post(`${API}/control/verify`, { passcode: code }); onVerified(code); }
    catch { setError("Invalid passcode"); }
    setLoading(false);
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="control-login">
      <motion.div className="w-full max-w-sm" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="RCCG" className="w-14 h-14 rounded-full mx-auto mb-4 shadow-lg" />
          <h1 className="font-heading font-bold text-xl text-navy">Control Centre</h1>
          <p className="font-body text-slate-400 text-sm mt-1">Enter passcode to continue</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-100 space-y-5" data-testid="passcode-form">
          <div className="space-y-2">
            <Label className="font-body font-semibold text-navy flex items-center gap-2"><Lock className="w-4 h-4" /> Passcode</Label>
            <Input data-testid="passcode-input" type="password" required value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter passcode" className="rounded-xl text-center text-2xl tracking-[0.3em] font-heading font-bold" />
          </div>
          {error && <p className="text-crimson text-sm font-body text-center">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-navy text-white font-body">{loading ? "Verifying..." : "Enter"}</Button>
          <a href="/" className="block text-center font-body text-slate-400 text-sm hover:text-navy">Back to Website</a>
        </form>
      </motion.div>
    </div>
  );
}

function ImageUploader({ passcode, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError("");
    setProgress(0);
    try {
      let uploadFile = file;
      if (file.type.startsWith("image/")) {
        uploadFile = await compressImage(file);
      }
      const result = await chunkedUpload(uploadFile, passcode, setProgress);
      onUploaded(`${process.env.REACT_APP_BACKEND_URL}${result.url}`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Upload failed";
      setError(msg);
    }
    setUploading(false);
    setProgress(0);
    if (fileRef.current) fileRef.current.value = "";
  };
  return (
    <div className="space-y-1">
      <Label className="font-body text-navy text-sm font-semibold flex items-center gap-2"><Image className="w-4 h-4" /> Event Poster</Label>
      <div className="flex items-center gap-3">
        <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-xl font-body text-sm">
          <Upload className="w-4 h-4 mr-1" /> {uploading ? `Uploading ${progress}%...` : "Upload Image"}
        </Button>
      </div>
      {error && <p className="font-body text-crimson text-xs">{error}</p>}
    </div>
  );
}

function EventsManager({ passcode }) {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", date: "", time: "", location: "Walton Community Hall, 274 High Street, Walton, Felixstowe, IP11 9DS", description: "", image: "", special: false });
  const fetchEvents = async () => { try { const { data } = await axios.get(`${API}/events`); setEvents(data); } catch {} };
  useEffect(() => { fetchEvents(); }, []);
  const createEvent = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/control/events/create`, { ...form, passcode }); setForm({ title: "", date: "", time: "", location: "Walton Community Hall, 274 High Street, Walton, Felixstowe, IP11 9DS", description: "", image: "", special: false }); setShowForm(false); fetchEvents(); } catch {}
  };
  const deleteEvent = async (id) => { try { await axios.delete(`${API}/control/events/${id}`, { data: { passcode } }); setEvents(prev => prev.filter(e => e.id !== id)); } catch {} };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading font-bold text-navy text-lg">Events ({events.length})</h3>
        <Button onClick={() => setShowForm(!showForm)} className="rounded-full bg-navy text-white font-body text-sm" data-testid="add-event-btn"><Plus className="w-4 h-4 mr-1" /> Add Event</Button>
      </div>
      {showForm && (
        <motion.form initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} onSubmit={createEvent} className="bg-slate-50 rounded-2xl p-6 mb-6 space-y-4 border border-slate-100" data-testid="event-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1"><Label className="font-body text-navy text-sm font-semibold">Title</Label><Input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="rounded-xl font-body" placeholder="Event title" /></div>
            <div className="space-y-1"><Label className="font-body text-navy text-sm font-semibold">Date</Label><Input type="date" required value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="rounded-xl font-body" /></div>
            <div className="space-y-1"><Label className="font-body text-navy text-sm font-semibold">Time</Label><Input value={form.time} onChange={(e) => setForm({...form, time: e.target.value})} className="rounded-xl font-body" placeholder="e.g. 11:00 AM" /></div>
            <div className="space-y-1"><Label className="font-body text-navy text-sm font-semibold">Location</Label><Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="rounded-xl font-body" /></div>
          </div>
          <div className="space-y-1"><Label className="font-body text-navy text-sm font-semibold">Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="rounded-xl font-body" rows={2} placeholder="Event details" /></div>
          <ImageUploader passcode={passcode} onUploaded={(url) => setForm({...form, image: url})} />
          {form.image && <img src={form.image} alt="Preview" className="w-24 h-24 rounded-xl object-cover" />}
          <div className="flex items-center gap-2">
            <input type="checkbox" id="special" checked={form.special} onChange={(e) => setForm({...form, special: e.target.checked})} className="w-4 h-4 rounded" />
            <Label htmlFor="special" className="font-body text-navy text-sm cursor-pointer">Special event (highlighted)</Label>
          </div>
          <div className="flex gap-3">
            <Button type="submit" className="rounded-full bg-cgreen text-white font-body text-sm">Save Event</Button>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-full font-body text-sm">Cancel</Button>
          </div>
        </motion.form>
      )}
      {events.length === 0 ? (
        <div className="text-center py-12 text-slate-400 font-body"><CalendarDays className="w-10 h-10 mx-auto mb-3 opacity-30" /><p>No events yet</p></div>
      ) : (
        <div className="space-y-3">
          {events.map((ev) => (
            <div key={ev.id} className={`bg-white rounded-2xl p-5 border shadow-sm flex items-start justify-between gap-4 ${ev.special ? "border-crimson/20" : "border-slate-100"}`}>
              <div className="flex gap-4 min-w-0 flex-1">
                {ev.image && <img src={ev.image} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />}
                <div className="min-w-0"><p className="font-heading font-bold text-navy text-sm">{ev.title}</p><p className="font-body text-slate-500 text-xs">{ev.date} {ev.time && `| ${ev.time}`}</p>{ev.description && <p className="font-body text-slate-400 text-xs mt-1 truncate">{ev.description}</p>}</div>
              </div>
              <button onClick={() => deleteEvent(ev.id)} className="p-2 rounded-full hover:bg-crimson/10 text-slate-400 hover:text-crimson transition-colors shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewsletterManager({ passcode }) {
  const [subs, setSubs] = useState([]);
  const [history, setHistory] = useState([]);
  const [view, setView] = useState("subscribers"); // subscribers | compose | history
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [sendToAll, setSendToAll] = useState(true);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [attachmentId, setAttachmentId] = useState("");
  const [attachmentName, setAttachmentName] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const fileRef = useRef(null);

  const fetchSubs = async () => { try { const { data } = await axios.get(`${API}/control/newsletter`, { headers: { "x-passcode": passcode } }); setSubs(data); } catch {} };
  const fetchHistory = async () => { try { const { data } = await axios.get(`${API}/control/newsletter/history`, { headers: { "x-passcode": passcode } }); setHistory(data); } catch {} };
  useEffect(() => { fetchSubs(); fetchHistory(); }, []); // eslint-disable-line

  const deleteSub = async (id) => { try { await axios.delete(`${API}/control/newsletter/${id}`, { data: { passcode } }); setSubs(prev => prev.filter(s => s.id !== id)); } catch {} };

  const uploadAttachment = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("passcode", passcode);
      const { data } = await axios.post(`${API}/control/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachmentId(data.id);
      setAttachmentName(file.name);
    } catch { alert("Upload failed"); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const sendNewsletter = async () => {
    if (!subject.trim() || !content.trim()) { alert("Subject and content required"); return; }
    setSending(true); setSendResult(null);
    try {
      const { data } = await axios.post(`${API}/control/newsletter/send`, {
        passcode,
        subject,
        html_content: content.replace(/\n/g, "<br/>"),
        send_to_all: sendToAll,
        recipients: sendToAll ? [] : selectedEmails,
        attachment_id: attachmentId,
      });
      setSendResult(data);
      fetchHistory();
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to send";
      setSendResult({ error: detail });
    }
    setSending(false);
  };

  const formatDate = (d) => { try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return d; } };

  const toggleEmail = (email) => {
    setSelectedEmails(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Button onClick={() => setView("subscribers")} variant={view === "subscribers" ? "default" : "outline"} className="rounded-full font-body text-sm"><Users className="w-4 h-4 mr-1" /> Subscribers ({subs.length})</Button>
        <Button onClick={() => setView("compose")} variant={view === "compose" ? "default" : "outline"} className="rounded-full font-body text-sm"><Send className="w-4 h-4 mr-1" /> Compose</Button>
        <Button onClick={() => setView("history")} variant={view === "history" ? "default" : "outline"} className="rounded-full font-body text-sm"><History className="w-4 h-4 mr-1" /> Sent ({history.length})</Button>
      </div>

      {/* SUBSCRIBERS */}
      {view === "subscribers" && (
        subs.length === 0 ? (
          <div className="text-center py-12 text-slate-400 font-body"><Users className="w-10 h-10 mx-auto mb-3 opacity-30" /><p>No subscribers yet</p></div>
        ) : (
          <div className="space-y-3">
            {subs.map((s) => (
              <div key={s.id} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex items-center justify-between">
                <div><p className="font-heading font-bold text-navy text-sm">{s.name || "No name"}</p><p className="font-body text-slate-400 text-xs">{s.email} &middot; {formatDate(s.subscribed_at)}</p></div>
                <button onClick={() => deleteSub(s.id)} className="p-2 rounded-full hover:bg-crimson/10 text-slate-400 hover:text-crimson transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )
      )}

      {/* COMPOSE */}
      {view === "compose" && (
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-5" data-testid="newsletter-compose">
          <div className="space-y-2">
            <Label className="font-body text-navy text-sm font-semibold">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-xl font-body" placeholder="Newsletter subject line" data-testid="nl-subject" />
          </div>
          <div className="space-y-2">
            <Label className="font-body text-navy text-sm font-semibold">Content</Label>
            <Textarea value={content} onChange={(e) => setContent(e.target.value)} className="rounded-xl font-body" rows={8} placeholder="Write your newsletter content here..." data-testid="nl-content" />
            <p className="text-xs text-slate-400 font-body">Line breaks will be preserved. HTML is supported.</p>
          </div>

          {/* Attachment */}
          <div className="space-y-2">
            <Label className="font-body text-navy text-sm font-semibold flex items-center gap-2"><FileText className="w-4 h-4" /> Attachment (optional)</Label>
            <div className="flex items-center gap-3">
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={uploadAttachment} className="hidden" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-xl font-body text-sm">
                <Upload className="w-4 h-4 mr-1" /> Upload File
              </Button>
              {attachmentName && (
                <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-xl">
                  <FileText className="w-4 h-4 text-navy" />
                  <span className="font-body text-sm text-navy">{attachmentName}</span>
                  <button onClick={() => { setAttachmentId(""); setAttachmentName(""); }} className="text-slate-400 hover:text-crimson"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>
          </div>

          {/* Recipients */}
          <div className="space-y-3">
            <Label className="font-body text-navy text-sm font-semibold">Recipients</Label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={sendToAll} onChange={() => setSendToAll(true)} className="w-4 h-4" />
                <span className="font-body text-sm text-navy">Send to all ({subs.length} subscribers)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={!sendToAll} onChange={() => setSendToAll(false)} className="w-4 h-4" />
                <span className="font-body text-sm text-navy">Select recipients</span>
              </label>
            </div>
            {!sendToAll && (
              <div className="max-h-40 overflow-y-auto space-y-1 bg-slate-50 rounded-xl p-3">
                {subs.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 cursor-pointer py-1">
                    <input type="checkbox" checked={selectedEmails.includes(s.email)} onChange={() => toggleEmail(s.email)} className="w-4 h-4 rounded" />
                    <span className="font-body text-sm text-navy">{s.name || s.email} <span className="text-slate-400">({s.email})</span></span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {sendResult && (
            <div className={`p-4 rounded-xl font-body text-sm ${sendResult.error ? "bg-crimson/10 text-crimson" : "bg-cgreen/10 text-cgreen"}`}>
              {sendResult.error
                ? (sendResult.error.includes("domain") || sendResult.error.includes("testing")
                  ? <>To send to all subscribers, verify your domain at <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="underline font-bold">resend.com/domains</a>. Free tier only sends to your own email.</>
                  : sendResult.sent > 0 ? `Sent to ${sendResult.sent}/${sendResult.total} (${sendResult.failed} failed)` : sendResult.error)
                : `Successfully sent to ${sendResult.sent}/${sendResult.total} recipients`}
            </div>
          )}

          <Button onClick={sendNewsletter} disabled={sending || !subject.trim() || !content.trim()} className="rounded-full bg-cgreen hover:bg-cgreen/90 text-white font-body" data-testid="nl-send-btn">
            <Send className="w-4 h-4 mr-2" /> {sending ? "Sending..." : sendToAll ? `Send to All (${subs.length})` : `Send to ${selectedEmails.length} selected`}
          </Button>
        </div>
      )}

      {/* HISTORY */}
      {view === "history" && (
        history.length === 0 ? (
          <div className="text-center py-12 text-slate-400 font-body"><History className="w-10 h-10 mx-auto mb-3 opacity-30" /><p>No newsletters sent yet</p></div>
        ) : (
          <div className="space-y-3">
            {history.map((h) => (
              <div key={h.id} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
                <p className="font-heading font-bold text-navy text-sm">{h.subject}</p>
                <p className="font-body text-slate-400 text-xs mt-1">Sent {formatDate(h.sent_at)} &middot; {h.sent}/{h.recipients_count} delivered{h.failed > 0 ? ` &middot; ${h.failed} failed` : ""}</p>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

// Compress image on client side before upload
async function compressImage(file, maxWidth = 1400, quality = 0.6) {
  if (!file.type.startsWith("image/")) return file;
  return new Promise((resolve) => {
    const img = new window.Image();
    const canvas = document.createElement("canvas");
    img.onload = () => {
      let w = img.width, h = img.height;
      if (w > maxWidth) { h = (h * maxWidth) / w; w = maxWidth; }
      if (h > maxWidth) { w = (w * maxWidth) / h; h = maxWidth; }
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => {
        if (blob && blob.size < file.size) {
          resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: "image/jpeg" }));
        } else {
          resolve(file);
        }
      }, "image/jpeg", quality);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });
}

// Chunked upload - splits file into 3MB pieces to bypass server limits
const CHUNK_SIZE = 3 * 1024 * 1024; // 3MB

async function chunkedUpload(file, passcode, onProgress) {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const uploadId = crypto.randomUUID ? crypto.randomUUID() : `u-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);
    
    const formData = new FormData();
    formData.append("file", chunk, `chunk_${i}`);
    formData.append("passcode", passcode);
    formData.append("upload_id", uploadId);
    formData.append("chunk_index", String(i));
    formData.append("total_chunks", String(totalChunks));
    formData.append("filename", file.name);
    formData.append("content_type", file.type);

    const resp = await axios.post(`${API}/control/upload/chunk`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000,
    });

    if (onProgress) onProgress(Math.round(((i + 1) / totalChunks) * 100));
    
    if (resp.data.status === "complete") {
      return resp.data;
    }
  }
  throw new Error("Upload did not complete");
}

function GalleryManager({ passcode }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [caption, setCaption] = useState("");
  const fileRef = useRef(null);

  const fetchPhotos = async () => {
    try { const { data } = await axios.get(`${API}/gallery`); setPhotos(data); } catch {}
  };
  useEffect(() => { fetchPhotos(); }, []);

  const cleanupBroken = async () => {
    try {
      const { data } = await axios.post(`${API}/control/gallery/cleanup`, { passcode });
      if (data.removed > 0) {
        setUploadError(`Removed ${data.removed} broken items`);
        fetchPhotos();
      } else {
        setUploadError("No broken items found");
      }
    } catch { setUploadError("Cleanup failed"); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      setUploadError("File too large (max 100MB)");
      return;
    }
    setUploading(true);
    setUploadError("");
    setUploadProgress(0);

    try {
      // Compress images, pass videos through
      let uploadFile = file;
      if (file.type.startsWith("image/")) {
        uploadFile = await compressImage(file);
      }

      // Use chunked upload for reliability
      const result = await chunkedUpload(uploadFile, passcode, setUploadProgress);

      // Register in gallery collection
      const isVideo = file.type.startsWith("video/");
      await axios.post(`${API}/control/gallery/register`, {
        passcode,
        file_id: result.id,
        file_url: result.url,
        caption: caption || file.name,
        type: isVideo ? "video" : "image",
        content_type: file.type,
      });

      setCaption("");
      setUploadError("");
      setUploadProgress(0);
      fetchPhotos();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Upload failed. Please try again.";
      setUploadError(msg);
      setUploadProgress(0);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const deletePhoto = async (id) => {
    try { await axios.delete(`${API}/control/gallery/${id}`, { data: { passcode } }); setPhotos(prev => prev.filter(p => p.id !== id)); } catch {}
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading font-bold text-navy text-lg">Gallery ({photos.length} photos)</h3>
        <Button onClick={cleanupBroken} variant="outline" className="rounded-full font-body text-xs text-slate-500" data-testid="gallery-cleanup-btn">
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove Broken Items
        </Button>
      </div>

      {/* Upload area */}
      <div className="bg-slate-50 rounded-2xl p-6 mb-6 border border-slate-100 space-y-4" data-testid="gallery-upload">
        <p className="font-body text-navy text-sm font-semibold">Add New Photo</p>
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
          <div className="flex-1 space-y-1 w-full">
            <Label className="font-body text-slate-500 text-xs">Caption (optional)</Label>
            <Input value={caption} onChange={(e) => setCaption(e.target.value)} className="rounded-xl font-body" placeholder="e.g. Sunday service worship" />
          </div>
          <div className="shrink-0">
            <input ref={fileRef} type="file" accept="image/*,video/*" onChange={uploadPhoto} className="hidden" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full bg-navy text-white font-body text-sm" data-testid="gallery-upload-btn">
              <Upload className="w-4 h-4 mr-1" /> {uploading ? `Uploading ${uploadProgress}%...` : "Upload Photo/Video"}
            </Button>
          </div>
        </div>
        {uploadError && <p className="font-body text-crimson text-sm">{uploadError}</p>}
        <p className="font-body text-slate-400 text-xs">Supports photos and videos up to 100MB</p>
      </div>

      {/* Photo grid */}
      {photos.length === 0 ? (
        <div className="text-center py-12 text-slate-400 font-body"><Camera className="w-10 h-10 mx-auto mb-3 opacity-30" /><p>No photos yet</p></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {photos.map((photo) => {
            const src = photo.image_url.startsWith("http") ? photo.image_url : `${process.env.REACT_APP_BACKEND_URL}${photo.image_url}`;
            const isVideo = photo.type === "video";
            return (
              <div key={photo.id} className="relative group rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
                {isVideo ? (
                  <video src={src} autoPlay muted loop playsInline className="w-full aspect-square object-cover" />
                ) : (
                  <img src={src} alt={photo.caption} className="w-full aspect-square object-cover" />
                )}
                {isVideo && (
                  <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-black/50 backdrop-blur-sm">
                    <Play className="w-3 h-3 text-white" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <button onClick={() => deletePhoto(photo.id)} className="opacity-0 group-hover:opacity-100 transition-opacity p-3 bg-white rounded-full shadow-lg text-crimson hover:bg-crimson hover:text-white" data-testid={`del-photo-${photo.id}`}>
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                  <p className="font-body text-white text-xs truncate">{photo.caption}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ControlCentre() {
  const [passcode, setPasscode] = useState(null);
  if (!passcode) return <PasscodeEntry onVerified={setPasscode} />;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="control-centre">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3"><img src={LOGO_URL} alt="RCCG" className="w-9 h-9 rounded-full" /><p className="font-heading font-bold text-navy text-sm">Control Centre</p></div>
          <div className="flex gap-3">
            <a href="/"><Button variant="outline" size="sm" className="rounded-full font-body text-xs">View Site</Button></a>
            <Button onClick={() => setPasscode(null)} variant="outline" size="sm" className="rounded-full font-body text-xs text-crimson border-crimson/30 hover:bg-crimson/5" data-testid="control-lock-btn"><Lock className="w-3.5 h-3.5 mr-1" /> Lock</Button>
          </div>
        </div>
      </header>
      <div className="max-w-5xl mx-auto px-6 py-8">
        <Tabs defaultValue="events" className="w-full">
          <TabsList className="rounded-full bg-slate-100 p-1 mb-6">
            <TabsTrigger value="events" className="rounded-full font-body text-sm"><CalendarDays className="w-4 h-4 mr-1" /> Events</TabsTrigger>
            <TabsTrigger value="gallery" className="rounded-full font-body text-sm"><Camera className="w-4 h-4 mr-1" /> Gallery</TabsTrigger>
            <TabsTrigger value="newsletter" className="rounded-full font-body text-sm"><Mail className="w-4 h-4 mr-1" /> Newsletter</TabsTrigger>
          </TabsList>
          <TabsContent value="events"><EventsManager passcode={passcode} /></TabsContent>
          <TabsContent value="gallery"><GalleryManager passcode={passcode} /></TabsContent>
          <TabsContent value="newsletter"><NewsletterManager passcode={passcode} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
